/**
 * Unified Transitions List for Context Panel
 * 
 * Displays artifact transitions for both Prime and Company artifacts.
 * Fetches from appropriate API endpoints based on artifact type.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ReloadOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { EnhancedArtifact } from '../../../types/artifacts';
import { PrimeArtifactService } from '../../../services/primeArtifactService';
import { TransitionService } from '../../../services/api/transitionService';

interface UnifiedTransitionsListProps {
  artifact: EnhancedArtifact;
  isPrime?: boolean;
  onTransitionClick?: (transitionId: string) => void;
}

export const UnifiedTransitionsList: React.FC<UnifiedTransitionsListProps> = ({
  artifact,
  isPrime,
  onTransitionClick
}) => {
  const [transitions, setTransitions] = useState<any[]>(artifact.state_transitions || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-detect if prime artifact
  const isPrimeArtifact = isPrime ?? (
    !artifact.artifact_tree_id || artifact.prime_artifact_id === artifact.id
  );

  const fetchTransitions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let fetchedTransitions;
      
      if (isPrimeArtifact) {
        // Fetch prime artifact transitions
        fetchedTransitions = await PrimeArtifactService.getPrimeArtifactTransitions(artifact.id);
      } else {
        // Fetch company artifact transitions
        fetchedTransitions = await TransitionService.getTransitionsByArtifact(artifact.id as any);
      }

      setTransitions(fetchedTransitions);
    } catch (err) {
      console.error('Failed to fetch transitions:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch transitions');
    } finally {
      setLoading(false);
    }
  }, [artifact.id, isPrimeArtifact]);

  useEffect(() => {
    // Only fetch if we don't have transitions already
    if (!transitions || transitions.length === 0) {
      void fetchTransitions();
    }
  }, []);

  const handleRefresh = () => {
    void fetchTransitions();
  };

  if (loading && transitions.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '16px',
        color: '#6b7280',
        fontSize: '12px'
      }}>
        Loading transitions...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontSize: '12px' }}>
        <div style={{
          padding: '8px',
          backgroundColor: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: '4px',
          color: '#991b1b',
          fontSize: '11px',
          marginBottom: '8px'
        }}>
          {error}
        </div>
        <button
          onClick={handleRefresh}
          style={{
            padding: '4px 12px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '11px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <ReloadOutlined style={{ fontSize: '12px' }} />
          Retry
        </button>
      </div>
    );
  }

  if (transitions.length === 0) {
    return (
      <div style={{ fontSize: '12px' }}>
        <div style={{ color: '#6b7280', fontStyle: 'italic', marginBottom: '8px' }}>
          No transitions defined
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          style={{
            padding: '4px 12px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '11px',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            opacity: loading ? 0.7 : 1
          }}
        >
          <ReloadOutlined style={{ fontSize: '12px' }} />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>
    );
  }

  return (
    <div style={{ fontSize: '12px' }}>
      {/* Header with refresh button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '8px'
      }}>
        <span style={{ fontSize: '11px', color: '#6b7280' }}>
          {transitions.length} transition{transitions.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={handleRefresh}
          disabled={loading}
          style={{
            padding: '2px 8px',
            backgroundColor: 'transparent',
            color: '#6b7280',
            border: '1px solid #d1d5db',
            borderRadius: '3px',
            fontSize: '10px',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <ReloadOutlined style={{ fontSize: '10px' }} />
          Refresh
        </button>
      </div>

      {/* Transitions List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {transitions.map(transition => (
          <div
            key={transition.id}
            onClick={() => onTransitionClick?.(transition.id)}
            style={{
              padding: '8px 10px',
              backgroundColor: '#f9fafb',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
              fontSize: '12px',
              cursor: onTransitionClick ? 'pointer' : 'default',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (onTransitionClick) {
                e.currentTarget.style.backgroundColor = '#f0f9ff';
                e.currentTarget.style.borderColor = '#3b82f6';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
          >
            <div style={{ 
              fontWeight: '500', 
              color: '#374151', 
              marginBottom: '4px'
            }}>
              {transition.name || 'Unnamed Transition'}
            </div>
            
            {/* Transition Flow */}
            {(transition.from_state_ref || transition.to_state_ref) && (
              <div style={{ 
                fontSize: '10px', 
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginBottom: '2px'
              }}>
                <span style={{
                  padding: '2px 4px',
                  backgroundColor: '#e0f2fe',
                  borderRadius: '3px'
                }}>
                  {getStateName(transition.from_state_ref, artifact)}
                </span>
                <ArrowRightOutlined style={{ fontSize: '10px' }} />
                <span style={{
                  padding: '2px 4px',
                  backgroundColor: '#dcfce7',
                  borderRadius: '3px'
                }}>
                  {getStateName(transition.to_state_ref, artifact)}
                </span>
              </div>
            )}
            
            {/* Transition Type */}
            {transition.default_transition_type && (
              <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                Type: {transition.default_transition_type}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper function to get state name
function getStateName(stateRef: string | undefined, artifact: EnhancedArtifact): string {
  if (!stateRef) return 'Unknown';
  
  const state = artifact.states?.find(s => s.id === stateRef);
  return state?.name || 'Unknown State';
}

